import React from "react";
import { Star, Quote, Heart } from "lucide-react";

const TestimonialsPage: React.FC = () => {
  const testimonials = [
    {
      name: "Sarah Johnson",
      relation: "Daughter of Garden Manor resident",
      facility: "Garden Manor",
      text: "My mother has been at Garden Manor for two years now, and I couldn't be happier with the care she receives. The memory care team is exceptional - they treat her with such dignity and compassion. The facility is beautiful, clean, and feels like home. I have complete peace of mind knowing she's in such capable hands.",
      rating: 5,
      image: "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg",
    },
    {
      name: "Michael Chen",
      relation: "Son of Sunrise Villa resident",
      facility: "Sunrise Villa",
      text: "Dad moved to Sunrise Villa after mom passed, and it was the best decision we could have made. He's made so many friends and stays active with all the activities. The river views from his apartment are gorgeous, and the staff goes above and beyond. He actually looks forward to meals now because the food is restaurant quality!",
      rating: 5,
      image: "https://images.pexels.com/photos/432059/pexels-photo-432059.jpeg",
    },
    {
      name: "Linda Rodriguez",
      relation: "Family member of Peaceful Pines resident",
      facility: "Peaceful Pines",
      text: "Peaceful Pines has given our family such peace of mind. Mom needed help with daily activities but wanted to keep her independence. The natural setting is perfect for her - she loves the nature trails and being able to bring her cat. The staff knows every resident personally and provides such individualized care.",
      rating: 5,
      image: "https://images.pexels.com/photos/428364/pexels-photo-428364.jpeg",
    },
    {
      name: "Robert Thompson",
      relation: "Son of Harmony Heights resident",
      facility: "Harmony Heights",
      text: "After Dad's stroke, we needed skilled nursing care but didn't want him in a typical nursing home. Harmony Heights exceeded all our expectations. The rehabilitation team helped him regain so much mobility, and the mountain views lift everyone's spirits. The medical care is outstanding, and they treat residents like family.",
      rating: 5,
      image: "https://images.pexels.com/photos/462680/pexels-photo-462680.jpeg",
    },
    {
      name: "Jennifer Walsh",
      relation: "Daughter of Garden Manor resident",
      facility: "Garden Manor",
      text: "When Mom was diagnosed with Alzheimer's, we were devastated and didn't know where to turn. Garden Manor not only provided excellent care for Mom but also supported our entire family. The memory care program is amazing, and they help maintain Mom's dignity while keeping her safe and engaged.",
      rating: 5,
      image: "https://images.pexels.com/photos/764001/pexels-photo-764001.jpeg",
    },
    {
      name: "David Martinez",
      relation: "Husband of Sunrise Villa resident",
      facility: "Sunrise Villa",
      text: "My wife and I moved to Sunrise Villa together when maintaining our home became too much. The independent living community here is vibrant and social. We've made lifelong friends, and the amenities are like living in a resort. The peace of mind knowing support is available when we need it is invaluable.",
      rating: 5,
      image: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg",
    },
    {
      name: "Angela Foster",
      relation: "Daughter of Peaceful Pines resident",
      facility: "Peaceful Pines",
      text: "Mom was hesitant about assisted living, but Peaceful Pines won her over immediately. The pet-friendly policy meant she could bring her beloved dog, and the nature setting reminds her of her childhood home. The care staff is so patient and kind, and they've helped her adjust beautifully to her new home.",
      rating: 5,
      image:
        "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg",
    },
    {
      name: "Thomas Wilson",
      relation: "Son of Harmony Heights resident",
      facility: "Harmony Heights",
      text: "The skilled nursing care at Harmony Heights is truly exceptional. After Dad's hip surgery, they provided comprehensive rehabilitation that got him back on his feet. The nursing staff is knowledgeable and attentive, and the facility feels more like a healing retreat than a medical facility. We're so grateful for their care.",
      rating: 5,
      image: "https://images.pexels.com/photos/547593/pexels-photo-547593.jpeg",
    },
  ];

  const stats = [
    { number: "98%", label: "Family Satisfaction Rate" },
    { number: "4.9/5", label: "Average Rating" },
    { number: "500+", label: "Happy Families" },
    { number: "25+", label: "Years of Excellence" },
  ];

  return (
    <div className="pt-8">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-green-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              What Families Say
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Don't just take our word for it. Hear from the families who have
              trusted us with their loved ones' care and discover why they chose
              Almond Tree.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary-green mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-8 relative"
              >
                <Quote className="absolute top-6 right-6 h-8 w-8 text-blue-200" />

                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {testimonial.name}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {testimonial.relation}
                    </p>
                    <p className="text-primary-green text-sm font-medium">
                      {testimonial.facility}
                    </p>
                  </div>
                </div>

                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-5 w-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>

                <p className="text-gray-700 leading-relaxed italic">
                  "{testimonial.text}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Testimonials Placeholder */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Video Testimonials
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Watch families share their experiences and see why Almond Tree has
              become a trusted name in senior living.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((index) => (
              <div
                key={index}
                className="relative bg-gray-200 rounded-lg overflow-hidden aspect-video group cursor-pointer"
              >
                <img
                  src={`https://images.pexels.com/photos/${
                    index === 0
                      ? "3768131"
                      : index === 1
                      ? "3768114"
                      : "3768113"
                  }/pexels-photo-${
                    index === 0
                      ? "3768131"
                      : index === 1
                      ? "3768114"
                      : "3768113"
                  }.jpeg`}
                  alt={`Video testimonial ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center group-hover:bg-opacity-30 transition-all duration-300">
                  <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <div className="w-0 h-0 border-l-8 border-r-0 border-t-6 border-b-6 border-l-blue-600 border-t-transparent border-b-transparent ml-1"></div>
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-semibold">Family Story {index + 1}</p>
                  <p className="text-sm opacity-90">2 minutes</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Summary */}
      <section className="py-20 bg-primary-green text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Heart className="h-16 w-16 text-white mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Why Families Choose Us</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
              <div className="bg-white bg-opacity-10 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-3">Exceptional Care</h3>
                <p className="opacity-90">
                  "The staff treats my mother like their own family member. The
                  level of personal attention is extraordinary."
                </p>
              </div>
              <div className="bg-white bg-opacity-10 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-3">
                  Beautiful Facilities
                </h3>
                <p className="opacity-90">
                  "Each location is gorgeous and well-maintained. It truly feels
                  like home, not an institution."
                </p>
              </div>
              <div className="bg-white bg-opacity-10 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-3">Peace of Mind</h3>
                <p className="opacity-90">
                  "Knowing Dad is safe, happy, and well-cared for has given our
                  entire family such relief and peace of mind."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Ready to Experience the Difference?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join the hundreds of families who trust Almond Tree with their loved
            ones. Schedule a tour and see why our residents and their families
            love calling us home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-primary-green text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-300">
              Schedule a Tour
            </button>
            <button className="px-8 py-4 border-2 border-blue-600 text-primary-green font-semibold rounded-lg hover:bg-primary-green hover:text-white transition-colors duration-300">
              Download Brochure
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;
